/**
 * 德育评价项目 store 模块
 */
import {
  getEvaluationProjects,
  getEvaluationProject,
  getEvaluationProjectList,
  getEvaluationProjectsByType,
  getEvaluationProjectWeights,
  createEvaluationProject as apiCreate,
  updateEvaluationProject as apiUpdate,
  deleteEvaluationProject as apiDelete
} from '@/api/evaluationProject'

/** 将 API 返回项转为前端格式 */
function normalizeItem(raw) {
  if (!raw) return null
  const defaultScore = raw.default_value ?? raw.default_score ?? raw.score ?? raw.weight ?? 0
  return {
    id: raw.evaluation_project_id ?? raw.project_id ?? raw.id,
    name: raw.name ?? raw.project_name ?? '',
    category: raw.evaluation_type_title ?? raw.type_name ?? raw.category ?? '',
    typeId: raw.evaluation_type_id ?? raw.type_id ?? raw.typeId,
    defaultScore: Number(defaultScore) || 0,
    defaultValue: Number(defaultScore) || 0,
    minScore: raw.min_value ?? raw.min_score ?? raw.minScore ?? 0,
    maxScore: raw.max_value ?? raw.max_score ?? raw.maxScore ?? 0,
    description: raw.description ?? '',
    isActive: raw.is_active ?? raw.isActive ?? true
  }
}

/** 转为 API 请求格式 */
function toApiPayload(item) {
  return {
    name: item.name,
    evaluation_type_id: item.typeId ?? item.type_id ?? item.evaluation_type_id,
    // The backend contract still uses `weight`, but its business meaning is now the default score.
    weight: Number(item.defaultScore ?? item.score ?? item.weight) || 0,
    description: (item.description ?? '').toString().trim()
  }
}

export default {
  namespaced: true,
  state: {
    list: [],
    weights: null,
    loading: false,
    error: null
  },
  getters: {
    evaluationProjects: state => state.list,
    evaluationProjectWeights: state => state.weights,
    loading: state => state.loading,
    error: state => state.error,
    /** 按分类筛选 */
    byCategory: state => category => {
      if (!category) return state.list
      return state.list.filter(p => p.category === category)
    }
  },
  mutations: {
    SET_LIST(state, list) {
      state.list = (list || []).map(normalizeItem).filter(Boolean)
    },
    SET_WEIGHTS(state, weights) {
      state.weights = weights
    },
    ADD_ITEM(state, item) {
      const n = normalizeItem(item)
      if (n) state.list.push(n)
    },
    UPDATE_ITEM(state, { id, data }) {
      const idx = state.list.findIndex(p => String(p.id) === String(id))
      if (idx !== -1) {
        state.list[idx] = { ...state.list[idx], ...normalizeItem(data) }
      }
    },
    REMOVE_ITEM(state, id) {
      state.list = state.list.filter(p => String(p.id) !== String(id))
    },
    SET_LOADING(state, v) {
      state.loading = !!v
    },
    SET_ERROR(state, err) {
      state.error = err
    }
  },
  actions: {
    /** 获取所有评价项目（API 可能直接返回数组） */
    async fetchList({ commit }) {
      commit('SET_LOADING', true)
      commit('SET_ERROR', null)
      try {
        const res = await getEvaluationProjects().catch(async (error) => {
          if (error?.status === 403 || error?.status === 404) {
            return getEvaluationProjectList()
          }
          throw error
        })
        const list = Array.isArray(res) ? res : (res.data ?? res.list ?? [])
        commit('SET_LIST', list)
        return list
      } catch (e) {
        commit('SET_ERROR', e.message)
        throw e
      } finally {
        commit('SET_LOADING', false)
      }
    },
    /** 获取指定项目 */
    async fetchOne(_, projectId) {
      const res = await getEvaluationProject(projectId)
      const data = res.data ?? res
      return normalizeItem(data)
    },
    /** 按类型获取项目 */
    async fetchByType(_, typeId) {
      const res = await getEvaluationProjectsByType(typeId)
      const list = res.data ?? res.list ?? (Array.isArray(res) ? res : [])
      return list.map(normalizeItem).filter(Boolean)
    },
    /** 获取权重信息 */
    async fetchWeights({ commit }) {
      const res = await getEvaluationProjectWeights()
      const data = res.data ?? res
      commit('SET_WEIGHTS', data)
      return data
    },
    /** 创建评价项目 */
    async create({ dispatch }, payload) {
      const data = toApiPayload(payload)
      await apiCreate(data)
      await dispatch('fetchList')
    },
    /** 更新评价项目 */
    async update({ dispatch }, { id, data }) {
      const payload = toApiPayload({ ...data, id })
      await apiUpdate(id, payload)
      await dispatch('fetchList')
    },
    /** 删除评价项目 */
    async remove({ dispatch }, id) {
      await apiDelete(id)
      await dispatch('fetchList')
    }
  }
}
